import { defineNuxtPlugin, isNuxtError, useRuntimeConfig } from "#imports";
import Rollbar from "rollbar";

const isValidAccessToken = (accessToken: unknown): accessToken is string => {
    return typeof accessToken === "string" && accessToken.length > 0;
};

// Pages deliberately throw 4xx errors (e.g. 404 for a distributor/manufacturer that no longer
// exists, 403 from route guards) to render the error page - that's expected flow, not a bug.
const isExpectedClientError = (error: unknown): boolean => {
    if (!isNuxtError(error)) return false;
    return typeof error.status === "number" && [403, 404].includes(error.status);
};

export default defineNuxtPlugin((nuxtApp) => {
    const config = useRuntimeConfig();
    const { clientAccessToken, config: rollbarConfig } = config.public.rollbar;
    const serverAccessToken = config.__rollbarServerAccessToken;

    const accessToken = import.meta.client ? clientAccessToken : serverAccessToken;

    if (!isValidAccessToken(accessToken)) {
        const side = import.meta.client ? "client" : "server";
        return console.error(
            `Skipping Rollbar initialization on ${side} side. Please provide a valid ${side}AccessToken.`
        );
    }

    const rollbar = new Rollbar({
        ...rollbarConfig,
        accessToken,
        payload: {
            ...rollbarConfig?.payload,
            client: {
                javascript: {
                    code_version: config.public.releaseVersion as string | undefined,
                },
            },
        },
    });

    // Called when a fatal error occurs
    nuxtApp.hook("app:error", (error) => {
        if (isExpectedClientError(error)) {
            return;
        }
        rollbar.error(error as Error);
    });

    return {
        provide: { rollbar },
    };
});
